import type { FormlyFieldConfig } from '@ngx-formly/core';

/**
 * Whether a `pages` section opens with a linked list that draws the section heading itself
 * (#22553): the title then shares one line with the list's add-button (the search field, when
 * shown, takes the line below), instead of sitting on a row of its own above the whole toolbar.
 *
 * "First field", not "only field": it is the same rule `DetailsService` uses to decide that such a
 * page has nothing of its own to edit (`isLinkedItemPage` → `hideSave` → `canEditEntry` →
 * `showEdit`: no Save, no Bewerken, no section pencil — so dropping the header row takes no pencil
 * with it), and a list that leads the page is the thing the reader came for whatever sits below it.
 *
 * Deliberately `linked-items` only: `required-items` leads a page the same way for `DetailsService`
 * but draws no search-and-add toolbar of this shape, so it keeps the classic header.
 *
 * A configured card-header action keeps the classic header: that header is where the action
 * button lives, and the list has no slot for it. So does a list that is hidden (a role, a
 * condition): it draws nothing, and the section would be left without any title.
 *
 * ONE predicate for both sides on purpose — `FormlyFieldPages` drops its title row on it and
 * `FormlyLinkedItemsComponent` draws the heading on it. If they ever disagreed the page would show
 * the title twice or not at all.
 */
export function pageLeadsWithLinkedItems(page: FormlyFieldConfig | undefined): boolean {
  const first = page?.fieldGroup?.[0];
  // `hide` and a `hidden` class are the two ways a node gets hidden. The class is a TOKEN test, not
  // `=== 'hidden'`: real config carries composite strings ('w-full float-left hidden ').
  const classHidden = (first?.className ?? '').split(/\s+/).includes('hidden');
  return first?.type === 'linked-items' && first.hide !== true && !classHidden && !page?.props?.['interfaceActionId'];
}

/**
 * The `pages` section `field` leads, if it is a linked list that draws that section's heading.
 * Undefined anywhere else: in a tab, in a plain group, further down a page, or in the edit face's
 * Overzicht clone (built as a root of its own, so it has no parent). The read face's Overzicht
 * card mounts the live field, which DOES have a parent — the component itself rules that case out
 * through `OVERVIEW_CARD_MOUNT`.
 */
export function leadingPageOf(field: FormlyFieldConfig): FormlyFieldConfig | undefined {
  const page = field.parent;
  return page?.parent?.type === 'pages' && page.fieldGroup?.[0] === field && pageLeadsWithLinkedItems(page)
    ? page
    : undefined;
}
